import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import PerformanceCard from './PerformanceCard';
import AlertBadge from './AlertBadge';

const RecouvrementSection = ({ data, onNavigate, formatCurrency }) => {
  const {
    montant_total_a_recouvrer,
    factures_en_attente,
    factures_en_retard,
    delai_moyen_paiement,
    top_clients_debiteurs,
    factures_retard_30_jours,
    montant_retard_30_jours,
    montant_recouvre_ce_mois,
    taux_recouvrement,
    relances_automatiques_planifiees,
    factures_par_statut,
    historique_relances
  } = data;

  // Données pour le graphique en secteurs des factures par statut
  const pieData = Object.entries(factures_par_statut).map(([statut, count]) => ({
    name: statut.replace('_', ' '),
    value: count,
    color: getStatusColor(statut)
  }));

  function getStatusColor(statut) {
    const colors = {
      'brouillon': '#6B7280',
      'envoyée': '#3B82F6',
      'acceptée': '#10B981',
      'payée': '#22C55E',
      'partiellement_payé': '#F59E0B',
      'en_retard': '#EF4444',
      'annulé': '#9CA3AF'
    };
    return colors[statut] || '#6B7280';
  }

  const getAlertLevel = (montant) => {
    if (montant > 10000000) return 'error'; // Plus de 10M
    if (montant > 5000000) return 'warning'; // Plus de 5M
    return 'neutral';
  };

  return (
    <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
        <span className="mr-3">💰</span>
        Factures & Recouvrement
      </h2>
      
      {/* Indicateurs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <AlertBadge
          type={getAlertLevel(montant_total_a_recouvrer)}
          title="Montant total à recouvrer"
          description={formatCurrency(montant_total_a_recouvrer)}
          icon="💰"
          pulse={montant_total_a_recouvrer > 10000000}
          onClick={() => onNavigate('receivables')}
        />
        
        <AlertBadge
          type={factures_en_attente > 15 ? "warning" : "neutral"}
          title="Factures en attente"
          count={factures_en_attente}
          description="Cliquez pour afficher la liste"
          icon="📋"
          onClick={() => onNavigate('pending-invoices')}
        />
        
        <PerformanceCard
          title="Montant recouvré ce mois"
          value={montant_recouvre_ce_mois}
          unit="FCFA"
          icon="✅"
          color="green"
          onClick={() => onNavigate('collected-this-month')}
        />
        
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {delai_moyen_paiement.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">jours</div>
            <div className="text-xs text-gray-500 mt-1">Délai moyen de paiement</div>
          </div>
        </div>
      </div>

      {/* Alertes critiques */}
      {(factures_retard_30_jours > 0 || relances_automatiques_planifiees > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <AlertBadge
            type="error"
            title={`Factures en retard > 30 jours`}
            description={`${formatCurrency(montant_retard_30_jours)} sur ${factures_retard_30_jours} factures`}
            count={factures_retard_30_jours}
            icon="🚨"
            pulse={true}
            onClick={() => onNavigate('overdue-invoices')}
          />
          
          <AlertBadge
            type="info"
            title="Relances automatiques planifiées"
            description="Pour les 7 prochains jours"
            count={relances_automatiques_planifiees}
            icon="📧"
            onClick={() => onNavigate('scheduled-reminders')}
          />
        </div>
      )}

      {/* Taux de recouvrement et graphique */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">
              {taux_recouvrement.toFixed(1)}%
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Taux de Recouvrement</h3>
            <p className="text-sm text-gray-600 mt-2">
              {Math.round(taux_recouvrement - 85)}% par rapport au mois précédent
            </p>
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Répartition des factures</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  dataKey="value"
                  label={({name, value}) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top clients débiteurs */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Top 5 Clients Débiteurs</h3>
          <button 
            onClick={() => onNavigate('all-debtors')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Voir tous →
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {top_clients_debiteurs.slice(0, 5).map((client, index) => (
            <div 
              key={index}
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => onNavigate('client-detail', { clientId: client.nom })}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900 truncate">{client.nom}</div>
                  <div className="text-sm text-gray-600">{client.nb_factures} facture(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-red-600">
                    {formatCurrency(client.montant)}
                  </div>
                  <div className="text-xs text-gray-500">à recouvrer</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('manual-reminder')}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
          >
            📧 Relance Manuel
          </button>
          
          <button
            onClick={() => onNavigate('mark-paid')}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
          >
            ✅ Marquer Payé
          </button>
          
          <button
            onClick={() => onNavigate('export-debtors')}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center"
          >
            📊 Export Excel
          </button>
          
          <button
            onClick={() => onNavigate('reminder-schedule')}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center"
          >
            ⏰ Programmer
          </button>
        </div>
      </div>

      {/* Historique des relances (si données disponibles) */}
      {historique_relances && historique_relances.length > 0 && (
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Dernières Relances</h3>
          <div className="space-y-2">
            {historique_relances.slice(0, 5).map((relance, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">{relance.date}</span>
                  <span className="font-medium">{relance.client}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    relance.type === 'email' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {relance.type === 'email' ? '📧 Email' : '📞 Téléphone'}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  relance.statut === 'envoyée' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {relance.statut}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default RecouvrementSection;